import { useState, useEffect, Fragment } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Link } from 'react-router-dom'
import { Alert } from '@mui/material'
import api from '../../utils/api'
import { formatDateTime } from '../../utils/datetime'
import LocationUpdatePayload from './LocationUpdatePayload'

interface StellantisEvent {
  id: number
  eventType: string
  vin: string | null
  timestamp: string | null
  receivedAt: string
  processed: boolean
  processedAt: string | null
  error: string
  contractId: string | null
  source: string
  vehicleId: number | null
  vehiclePlate: string | null
  payload: Record<string, any> | null
}

interface StellantisEventsResponse {
  count: number
  next: string | null
  previous: string | null
  results: StellantisEvent[]
}

type OrderDirection = 'asc' | 'desc'

const StellantisEventsView = () => {
  const [events, setEvents] = useState<StellantisEvent[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [searchQuery, setSearchQuery] = useState('')
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('')
  const [processedFilter, setProcessedFilter] = useState<string>('')
  const [orderBy, setOrderBy] = useState('received_at')
  const [orderDirection, setOrderDirection] = useState<OrderDirection>('desc')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const fetchEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      params.set('page', String(page + 1))
      params.set('page_size', String(rowsPerPage))

      if (searchQuery) params.set('search', searchQuery)
      if (eventTypeFilter) params.set('event_type', eventTypeFilter)
      if (processedFilter) params.set('processed', processedFilter)

      const ordering = orderDirection === 'desc' ? `-${orderBy}` : orderBy
      params.set('ordering', ordering)

      const response = await api.get<StellantisEventsResponse>(
        `/stellantis/events/browse/?${params.toString()}`
      )
      setEvents(response.data.results)
      setTotalCount(response.data.count)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 403) {
        setError('You do not have permission to view Stellantis events. This page requires superuser access.')
      } else if (status === 401) {
        setError('Your session has expired. Please log in again.')
      } else {
        setError('Failed to load Stellantis events. Please try again later.')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [page, rowsPerPage, searchQuery, eventTypeFilter, processedFilter, orderBy, orderDirection])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
    setPage(0)
  }

  const handleEventTypeChange = (event: any) => {
    setEventTypeFilter(event.target.value)
    setPage(0)
  }

  const handleProcessedChange = (event: any) => {
    setProcessedFilter(event.target.value)
    setPage(0)
  }

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(field)
      setOrderDirection('desc')
    }
    setPage(0)
  }

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Stellantis Raw Events
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Search"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by VIN, contract ID, error..."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Event Type</InputLabel>
            <Select
              value={eventTypeFilter}
              label="Event Type"
              onChange={handleEventTypeChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="vehicle_data">Vehicle Data</MenuItem>
              <MenuItem value="location_update">Location Update</MenuItem>
              <MenuItem value="vehicle_status">Vehicle Status</MenuItem>
              <MenuItem value="diagnostics">Diagnostics</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Processed</InputLabel>
            <Select
              value={processedFilter}
              label="Processed"
              onChange={handleProcessedChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Processed</MenuItem>
              <MenuItem value="false">Not Processed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'event_type'}
                  direction={orderBy === 'event_type' ? orderDirection : 'asc'}
                  onClick={() => handleSort('event_type')}
                >
                  Event Type
                </TableSortLabel>
              </TableCell>
              <TableCell>VIN</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'received_at'}
                  direction={orderBy === 'received_at' ? orderDirection : 'asc'}
                  onClick={() => handleSort('received_at')}
                >
                  Received At
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'timestamp'}
                  direction={orderBy === 'timestamp' ? orderDirection : 'asc'}
                  onClick={() => handleSort('timestamp')}
                >
                  Timestamp
                </TableSortLabel>
              </TableCell>
              <TableCell>Processed</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Contract ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => {
              const isExpanded = expandedRows.has(event.id)
              return (
                <Fragment key={event.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                    onClick={() => toggleRow(event.id)}
                  >
                    <TableCell padding="checkbox">
                      <IconButton size="small">
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{event.eventType}</TableCell>
                    <TableCell>
                      {event.vin ? (
                        event.vehicleId ? (
                          <Link to={`/vehicles/${event.vehicleId}/`} onClick={(e) => e.stopPropagation()}>
                            {event.vin}
                          </Link>
                        ) : (
                          event.vin
                        )
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(event.receivedAt)}</TableCell>
                    <TableCell>{event.timestamp ? formatDateTime(event.timestamp) : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={event.processed ? 'Yes' : 'No'}
                        color={event.processed ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {event.error || '-'}
                    </TableCell>
                    <TableCell>{event.contractId || '-'}</TableCell>
                  </TableRow>
                  <TableRow key={`${event.id}-payload`}>
                    <TableCell sx={{ py: 0 }} colSpan={8}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Payload
                          </Typography>
                          {event.eventType === 'location_update' && event.payload ? (
                            <LocationUpdatePayload payload={event.payload} />
                          ) : (
                            <Box
                              component="pre"
                              sx={{
                                bgcolor: 'grey.100',
                                p: 2,
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: 400,
                                fontSize: '0.8rem',
                                m: 0,
                              }}
                            >
                              {JSON.stringify(event.payload, null, 2)}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  )
}

export default StellantisEventsView
